import { useQuery } from '@powersync/react';
import { useNavigate } from '@tanstack/react-router';

import { queries, type InboxItemRow, type WorkspaceSummary } from '@flow/sync';
import { InboxList } from '@flow/ui/components';

import { formatDay } from '../capture/model.js';
import { useToday } from '../capture/use-today.js';
import { t } from '../i18n/es.js';

/**
 * Content of the Inbox view (docs/04 §2 rule 1): the open elements of the capture stage, oldest
 * first, and the way into «Procesar Inbox».
 */

export interface InboxTag {
  id: string;
  name: string;
  prefix: string | null;
}

/** Tags of an Inbox row (a JSON array built by the query). */
export function inboxTags(row: Pick<InboxItemRow, 'tags'>): InboxTag[] {
  try {
    const parsed = JSON.parse(row.tags) as InboxTag[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const tagLabel = (tag: InboxTag) => `${tag.prefix ?? ''}${tag.name}`;

export function InboxView({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const today = useToday(userId);
  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<WorkspaceSummary>(
    workspaceQuery.sql,
    workspaceQuery.params,
  );
  const stageQuery = queries.captureStage(workspaces[0]?.id ?? '');
  const { data: stages } = useQuery<{ id: string }>(stageQuery.sql, stageQuery.params);
  const itemsQuery = queries.inboxItems(stages[0]?.id ?? '');
  const { data: items, isLoading } = useQuery<InboxItemRow>(itemsQuery.sql, itemsQuery.params);

  if (isLoading && items.length === 0) return null;
  return (
    <InboxList
      items={items.map((item) => ({
        id: item.id,
        title: item.title,
        due: item.due_on ? t.inbox.due(formatDay(item.due_on, today)) : null,
        tags: inboxTags(item).map(tagLabel),
      }))}
      onProcess={() => void navigate({ to: '/triage' })}
      labels={{
        list: t.inbox.list,
        process: t.inbox.process,
        empty: t.inbox.empty,
        count: t.inbox.count,
      }}
    />
  );
}
